import React from 'react';
import { InputNumber, message } from 'antd';
import { FJ730 } from '../../../services/j218Api';
import LookupField from '../../../components/LookupField';

interface Tab3Props {
  getField: (key: keyof FJ730) => unknown;
  setField: (key: keyof FJ730, value: unknown) => void;
  isEditing: boolean;
}

const numF = (
  getField: (key: keyof FJ730) => unknown,
  setField: (key: keyof FJ730, value: unknown) => void,
  key: keyof FJ730,
  isEditing: boolean,
  _step?: string,
) => (
  <InputNumber value={Number(getField(key) || 0)}
    disabled={!isEditing}
    onChange={v => setField(key, v || 0)} />
);

const strF = (
  getField: (key: keyof FJ730) => unknown,
  setField: (key: keyof FJ730, value: unknown) => void,
  key: keyof FJ730,
  isEditing: boolean,
) => (
  <input value={String(getField(key) || '')} disabled={!isEditing}
    onChange={e => setField(key, e.target.value)} />
);

const Tab3Product: React.FC<Tab3Props> = React.memo(({ getField, setField, isEditing }) => {
  const handleReportPreview = (field: keyof FJ730) => {
    const code = String(getField(field) || '');
    if (!code.trim()) {
      message.warning('請先填入報告單號');
      return;
    }
    message.info(`物化性報告預覽：${code}（功能尚未實作）`);
  };

  return (
    <div className="tab3-product">
      {/* Region: Shape2 — 染整廠 */}
      <div className="section-frame">
        <h4>染整廠 #1</h4>
        <div className="form-grid-compact">
          <label>工繳代號</label>
          <LookupField lookupType="expenseCode" value={String(getField('fj730_301') || '')}
            onChange={(code) => setField('fj730_301', code)} disabled={!isEditing} title="選擇工繳代號" />
          <label>大貨價</label>{numF(getField, setField, 'fj730_302', isEditing)}
          <label>試樣價</label>{numF(getField, setField, 'fj730_303', isEditing)}
          <label>單位</label>{strF(getField, setField, 'fj730_304', isEditing)}
          <label>縮率%</label>{numF(getField, setField, 'fj730_305', isEditing, '0.01')}
          <label>備註</label>{strF(getField, setField, 'fj730_306', isEditing)}
          <label>供應商</label>
          <LookupField lookupType="supplier" value={String(getField('fj730_307') || '')}
            onChange={(code) => setField('fj730_307', code)} disabled={!isEditing} title="選擇供應商" />
        </div>

        <h4>染整廠 #2</h4>
        <div className="form-grid-compact">
          <label>工繳代號</label>
          <LookupField lookupType="expenseCode" value={String(getField('fj730_311') || '')}
            onChange={(code) => setField('fj730_311', code)} disabled={!isEditing} title="選擇工繳代號" />
          <label>大貨價</label>{numF(getField, setField, 'fj730_312', isEditing)}
          <label>試樣價</label>{numF(getField, setField, 'fj730_313', isEditing)}
          <label>單位</label>{strF(getField, setField, 'fj730_314', isEditing)}
          <label>縮率%</label>{numF(getField, setField, 'fj730_315', isEditing, '0.01')}
          <label>備註</label>{strF(getField, setField, 'fj730_316', isEditing)}
          <label>供應商</label>
          <LookupField lookupType="supplier" value={String(getField('fj730_317') || '')}
            onChange={(code) => setField('fj730_317', code)} disabled={!isEditing} title="選擇供應商" />
        </div>

        <h4>染整廠 #3</h4>
        <div className="form-grid-compact">
          <label>工繳代號</label>
          <LookupField lookupType="expenseCode" value={String(getField('fj730_321') || '')}
            onChange={(code) => setField('fj730_321', code)} disabled={!isEditing} title="選擇工繳代號" />
          <label>大貨價</label>{numF(getField, setField, 'fj730_322', isEditing)}
          <label>試樣價</label>{numF(getField, setField, 'fj730_323', isEditing)}
          <label>單位</label>{strF(getField, setField, 'fj730_324', isEditing)}
          <label>縮率%</label>{numF(getField, setField, 'fj730_325', isEditing, '0.01')}
          <label>備註</label>{strF(getField, setField, 'fj730_326', isEditing)}
          <label>供應商</label>
          <LookupField lookupType="supplier" value={String(getField('fj730_327') || '')}
            onChange={(code) => setField('fj730_327', code)} disabled={!isEditing} title="選擇供應商" />
        </div>
      </div>

      {/* Region: Shape3 — 成品/物化性檢測 */}
      <div className="section-frame">
        <h4>成品/物化性檢測</h4>
        <div className="form-grid-compact">
          <label>組織</label>{strF(getField, setField, 'fj730_51', isEditing)}
          <label>幅寬(內)</label>{numF(getField, setField, 'fj730_521', isEditing, '0.1')}
          <label>幅寬(外)</label>{numF(getField, setField, 'fj730_522', isEditing, '0.1')}
          <label>成份</label>{numF(getField, setField, 'fj730_531', isEditing, '1')}
          <label>%</label>{numF(getField, setField, 'fj730_532', isEditing, '1')}
          <label>%</label>{numF(getField, setField, 'fj730_533', isEditing, '1')}
          <label>重量</label>{numF(getField, setField, 'fj730_541', isEditing, '0.1')}
          <label>g/㎡</label>{numF(getField, setField, 'fj730_542', isEditing, '0.1')}
          <label>工繳代號</label>
          <LookupField lookupType="expenseCode" value={String(getField('fj730_55') || '')}
            onChange={(code) => setField('fj730_55', code)} disabled={!isEditing} title="選擇工繳代號" />
          <label>克重</label>{numF(getField, setField, 'fj730_561', isEditing)}
          <label>成品單位</label>
          <LookupField lookupType="product" value={String(getField('fj730_562') || '')}
            onChange={(code) => setField('fj730_562', code)} disabled={!isEditing} title="選擇成品單位" />
          <label>供應商</label>
          <LookupField lookupType="supplier" value={String(getField('fj730_57') || '')}
            onChange={(code) => setField('fj730_57', code)} disabled={!isEditing} title="選擇供應商" />
          <label>工廠布號</label>{strF(getField, setField, 'fj730_571', isEditing)}
        </div>
      </div>

      <h4>物化性報告</h4>
      <div className="form-grid-compact">
        <label>報告#1</label>
        <div className="report-field-row">
          <LookupField lookupType="sampleAnalysis" value={String(getField('fj730_61') || '')}
            onChange={(code) => setField('fj730_61', code)} disabled={!isEditing} title="選擇物化性報告" />
          <button className="btn-report-preview" disabled={!getField('fj730_61')}
            onClick={() => handleReportPreview('fj730_61')}>查詢</button>
        </div>
        <label>報告#2</label>
        <div className="report-field-row">
          <LookupField lookupType="sampleAnalysis" value={String(getField('fj730_611') || '')}
            onChange={(code) => setField('fj730_611', code)} disabled={!isEditing} title="選擇物化性報告" />
          <button className="btn-report-preview" disabled={!getField('fj730_611')}
            onClick={() => handleReportPreview('fj730_611')}>查詢</button>
        </div>
        <label>報告#3</label>
        <div className="report-field-row">
          <LookupField lookupType="sampleAnalysis" value={String(getField('fj730_612') || '')}
            onChange={(code) => setField('fj730_612', code)} disabled={!isEditing} title="選擇物化性報告" />
          <button className="btn-report-preview" disabled={!getField('fj730_612')}
            onClick={() => handleReportPreview('fj730_612')}>查詢</button>
        </div>
      </div>
    </div>
  );
});

Tab3Product.displayName = 'Tab3Product';
export default Tab3Product;
